import React, { FC, useState, useEffect, ChangeEvent } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Select,
    MenuItem,
    TextField,
    Chip,
    SelectChangeEvent,
    Grid,
} from '@mui/material';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { OrderStep, Steps } from 'models/workflow-step';
import { changeWorkflowStepTemplate } from 'apollo/templates/workFlow';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order, SampleType } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { Organization } from 'models/organization';
import { User } from 'models/user';
import { ApolloCache, Reference } from '@apollo/client/core';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { millingDeadlineDate } from 'utils/milling-deadline-date';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { getOrderDetailsTemplate } from 'apollo/templates/order';

type ChangeWorkflowStepDialogProps = {
    onClose: () => void;
    open: boolean;
    performMutation: boolean;
    orderId?: string;
    onUpdateStep?: (orderId: string | undefined, step: OrderStepInput, millingDeadline: Date | null) => void;
};

const ChangeWorkflowStepDialog: FC<ChangeWorkflowStepDialogProps> = ({
    onClose,
    open,
    performMutation,
    orderId,
    onUpdateStep,
}: ChangeWorkflowStepDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'workflow', 'milling-jobs']);
    const currentOrg = currentOrgVar();
    const currentUser = currentUserVar();
    const [currentStep, setCurrentStep] = useState<Steps>({} as Steps);
    // DD-3391: no external tier may read the lab's user list, so asking for it is a
    // hard ACCESS_DENIED. They are the technician on their own work anyway, so the
    // picker is seeded with them rather than left showing a raw id.
    const isExternalUser = permissions.isExternalTechnician(currentUser.role);
    const [users, setUsers] = useState<User[]>(isExternalUser ? [currentUser] : []);
    const [userId, setUserId] = useState(currentUser._id);
    const [comment, setComment] = useState('');
    const [steps, setSteps] = useState<Steps[]>(currentOrg.workFlow?.steps || []);
    const [currentStepIndex, setCurrentStepIndex] = useState(-1);
    const [deadline, setDeadline] = useState<Date | null>(null);
    const { showBoundary } = useDenthubErrorBoundary();
    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );
    const [labAvailability, setLabAvailability] = useState<Organization>();
    const [order, setOrder] = useState<Order>();

    const [getOrder] = useLazyQuery(getOrderDetailsTemplate, {
        onCompleted({ order }) {
            setOrder(order);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (orderId) {
            getOrder({
                variables: {
                    orderId: orderId,
                },
            });
        }
    }, [orderId]);

    useEffect(() => {
        setInitialData();
    }, [order]);

    useQuery(getTechniciansForUserListTemplate, {
        skip: isExternalUser,
        variables: {
            labId: (currentUser.organization as Organization)?._id,
            filters: { active: true },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setUsers(technicians);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useQuery(labSettingsTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ currentLab }) {
            setLabAvailability({ ...currentLab });
        },
    });

    useEffect(() => {
        if (
            isLabMillingEnabled &&
            currentStep.useMillingDeadline &&
            currentStep.millingDeadlineDays &&
            labAvailability
        ) {
            const deadlineDate = millingDeadlineDate(currentStep.millingDeadlineDays as number, labAvailability);
            setDeadline(deadlineDate as Date);
        } else {
            setDeadline(null);
        }
    }, [currentStep, labAvailability]);

    const setInitialData = () => {
        if (!currentOrg.workFlow) {
            return;
        }
        setSteps([...currentOrg.workFlow.steps]);
        const defaultStep =
            !order?.workFlow?.currentStep?.stepId &&
            currentOrg.workFlow.steps.find((step) => {
                if (order?.deliveryDetails?.sampleType === SampleType.NO_SAMPLE) {
                    return currentOrg.workFlow?.defaultNoSample === step._id;
                }
                if (order?.deliveryDetails?.sampleType === SampleType.DIGITAL) {
                    return currentOrg.workFlow?.defaultDigitalSample === step._id;
                }
                if (order?.deliveryDetails?.sampleType === SampleType.PHYSICAL) {
                    return currentOrg.workFlow?.defaultPhysicalSample === step._id;
                }
                return false;
            });
        if (order?.workFlow?.currentStep) {
            const temp = [...currentOrg.workFlow.steps];
            const index = temp.findIndex((step: Steps) => step._id === order.workFlow.currentStep?.stepId);
            if (index > -1) {
                temp.splice(index, 1);
                setCurrentStepIndex(index);
                setSteps([...temp]);
            }
        }
        setCurrentStep((defaultStep || {}) as Steps);
    };

    const [changeWorkflowStep] = useMutation(changeWorkflowStepTemplate, {
        onCompleted: ({ changeWorkflowStep }) => {
            onClose();
            onUpdateStep?.(changeWorkflowStep._id, createOrderStepInput(), deadline);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<Order[]>, { data: { changeWorkflowStep } }) {
            cache.modify({
                fields: {
                    stepOrders(existingStepOrderRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingStepOrderRefs instanceof Array) {
                            return [...existingStepOrderRefs].filter((stepOrderRef: Reference | undefined) => {
                                return order?._id !== readField('_id', stepOrderRef);
                            });
                        }
                        return [];
                    },
                    orderWorkflow() {
                        return changeWorkflowStep.workFlow.stepLogs;
                    },
                },
            });
        },
    });

    const handleStepClick = (step: Steps) => () => {
        setCurrentStep(step);
    };

    const handleUserChange = (event: SelectChangeEvent) => {
        setUserId(event.target.value);
    };

    const handleChangeStep = async () => {
        const orderCurrentStep = createOrderStepInput();
        if (performMutation && order?._id) {
            await changeWorkflowStep({ variables: { orderCurrentStep } });
            return;
        } else if (onUpdateStep) {
            onUpdateStep(order?._id, orderCurrentStep, deadline);
        }
        onClose();
    };

    const onCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setComment(value);
    };

    const handleClose = () => {
        onClose();
        setInitialData();
        setUserId(currentUser._id);
        setComment('');
    };

    const handleDateChange = (date: Date | null) => {
        setDeadline(date as Date);
    };

    const createOrderStepInput = (): OrderStepInput => {
        const defaultExtUsers = currentStep.defaultExtUsers?.map((user) => (user as User)._id);
        return {
            orderId: order?._id,
            stepId: currentStep._id,
            name: currentStep.name,
            description: currentStep.description,
            comment: comment,
            maxHourInStep: currentStep.maxHourInStep,
            userId: userId,
            extUsers: defaultExtUsers,
            millingDeadline: deadline,
            useMillingDeadline: currentStep.useMillingDeadline,
        };
    };

    return (
        <div>
            <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
                <DialogTitle>{translate('orders:CHANGE_STEP')}</DialogTitle>
                <DialogContent>
                    <Box>
                        {order?.workFlow?.currentStep ? (
                            <Box mb={2}>
                                <Typography variant="subtitle2">{translate('orders:CURRENT_STEP')}</Typography>
                                <Chip
                                    size="medium"
                                    label={
                                        order?.workFlow?.currentStep?.name
                                            ? `${currentStepIndex + 1} - ${order?.workFlow?.currentStep?.name}`
                                            : translate('workflow:NO_STEP')
                                    }
                                    color="info"
                                />
                            </Box>
                        ) : undefined}
                        <Typography variant="subtitle2">{translate('orders:SELECT_NEW_WORKFLOW_STEP')}</Typography>
                        <Box mt={1} display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
                            {order?.workFlow?.currentStep?.stepId ? (
                                <Chip
                                    size="medium"
                                    clickable
                                    label={translate('workflow:NO_STEP')}
                                    color={!currentStep?._id ? 'primary' : undefined}
                                    onClick={handleStepClick({} as Steps)}
                                />
                            ) : undefined}
                            {steps?.map((step, index) => (
                                <Chip
                                    key={index}
                                    size="medium"
                                    clickable
                                    color={currentStep?._id === step._id ? 'primary' : undefined}
                                    label={`${index + 1} - ${step.name}`}
                                    onClick={handleStepClick(step)}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Box mt={2}>
                        <Typography variant="subtitle2">{translate('USER')}</Typography>
                        <Select
                            fullWidth
                            value={userId}
                            onChange={handleUserChange}
                            renderValue={(value) => {
                                const item = users.find(({ _id: v }) => v === value);
                                return item ? `${item.name.firstName} ${item.name.lastName}` : value;
                            }}
                        >
                            {users.map((user, index) => (
                                <MenuItem value={user._id} key={index}>
                                    {`${user.name.firstName} ${user.name.lastName}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box mt={2}>
                        <Grid container rowSpacing={3} columnSpacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    onChange={onCommentChange}
                                    label={translate('common:COMMENTS')}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {isLabMillingEnabled && currentStep.useMillingDeadline ? (
                        <Box mt={2}>
                            <Grid container rowSpacing={3} columnSpacing={2}>
                                <Grid item md={4}>
                                    <Typography variant="subtitle2">
                                        {translate('milling-jobs:MILLING_DEADLINE')}
                                    </Typography>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                        adapterLocale={getDateFnsLocale()}
                                    >
                                        <div>
                                            <DatePicker
                                                format={getDatePickerFormat()}
                                                minDate={new Date()}
                                                value={deadline}
                                                onChange={handleDateChange}
                                            />
                                        </div>
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : undefined}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{translate('CANCEL')}</Button>
                    <Button disabled={!userId} variant="contained" onClick={handleChangeStep}>
                        {translate('orders:CHANGE_STEP')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export type OrderStepInput = {
    orderId?: string;
    stepId?: string;
    name: string;
    description: string;
    comment: string;
    maxHourInStep: number;
    userId: string;
    extUsers: string[];
    millingDeadline: Date | null;
    useMillingDeadline?: boolean;
};

export default ChangeWorkflowStepDialog;
